
import { GeoAddressArea } from '@/domain/entities/ManageArea';
import Tree from "./tree.vue";
import { Options, prop, Vue } from 'vue-class-component';

class Props {
  options = prop<GeoAddressArea[]>({
    default: () => [],
    type: Array
  });
  modelValue = prop<number[]>({
    default: () => [],
    type: Array
  });
  loading = prop<boolean>({
    default: false,
    type: Boolean
  });
  isError = prop<boolean>({
    default: false,
    type: Boolean
  });
  errorMessage = prop<string>({
    default: "",
    type: String
  });
  shipmentType = prop<string>({
    default: "Marketplace & Klien",
    type: String
  });
  excludeGeoAddressIds = prop<number[]>({
    default: () => [],
    type: Array
  });
}

@Options({
  components: {
    Tree
  }
})
class GeoAddressTreeSelector extends Vue.with(Props) {
  isOpenSelect = false;
  keyword = "";

  get filteredOptions(): GeoAddressArea[] {
    return this.handleExcludeGeoAddressByIds(this.handleFilterOptions())
  }

  get excludedGeoAddressIds() {
    const ids: number[] = []

    this.excludeGeoAddressIds.forEach((id) => {
      if (!this.modelValue.includes(id)) ids.push(id)
    })

    return ids;
  }

  get model() {
    return this.modelValue;
  }

  set model(val: number[]) {
    this.$emit("update:modelValue", val);
  }

  handleExcludeGeoAddressByIds(nodes: GeoAddressArea[], excludedIds: number[] = this.excludedGeoAddressIds): GeoAddressArea[] {
    const excludedSet = new Set(excludedIds);

    return nodes.reduce<GeoAddressArea[]>((acc, node) => {
      if (node.geoAddressId !== undefined && excludedSet.has(node.geoAddressId)) {
        return acc;
      }

      const clonedNode: GeoAddressArea = new GeoAddressArea({ ...node });

      if (node.children && node.children.length > 0) {
        const filteredChildren = this.handleExcludeGeoAddressByIds(node.children, excludedIds);

        if (filteredChildren.length > 0) {
          clonedNode.children = filteredChildren;
          acc.push(clonedNode);
        }
      } else {
        acc.push(clonedNode);
      }

      return acc;
    }, []);
  }

  handleFilterOptions(options: GeoAddressArea[] = this.options, keyword = this.keyword) {
    return options.filter(option => {
      return option
    }).map(option => {
      const clonedOption = new GeoAddressArea({ ...option });

      const isMatch = option.geoAddressId && option.name.toLowerCase().includes(keyword.toLowerCase());

      if (option.children && option.children.length > 0) {
        const filteredChildren = this.handleFilterOptions(option.children, keyword);

        const hasMatchingChild = filteredChildren.some(child => !child.isHidden);

        clonedOption.children = filteredChildren;
        clonedOption.isHidden = !isMatch && !hasMatchingChild;
      } else {
        clonedOption.isHidden = !isMatch;
      }

      return clonedOption;
  });
  }

  openSelect() {
    this.isOpenSelect = true;
  }

  closeSelect() {
    this.isOpenSelect = false;
    this.keyword = "";
  }

  deletedSelected(event: any, geoAddress: GeoAddressArea) {
    event.stopPropagation();
    this.handleDeleteSelected(geoAddress);
  }

  handleDeleteSelected(geoAddress: GeoAddressArea) {
    if (geoAddress.geoAddressId) {
      this.model.splice(this.model.findIndex(id => geoAddress.geoAddressId === id), 1);
    } else {
      for (const child of geoAddress.children) {
        this.handleDeleteSelected(child);
      }
    }
  }

  mapNames(options = this.options): GeoAddressArea[] {
    return options
      .map(ga => {
        if (ga.isChecked(this.model)) {
          return ga;
        }
        const children = ga.children ?? [];
        if (!children.length) {
          return undefined;
        }
        return this.mapNames(children);
      })
      .flat()
      .filter((item): item is GeoAddressArea => Boolean(item));
  }
}

export default GeoAddressTreeSelector;
